/**
 * Single source of truth for "the browser is currently on a public, PIN-gated report link"
 * (`/report-view/:slug`, `/report-preview/:slug`).
 *
 * These URLs are emailed to CUSTOMERS who have no Remmi account. Nothing on this path may
 * redirect to `/login`, clear storage, or hard-navigate — the page must always end up either
 * showing the PIN gate or the report.
 *
 * Previously each caller re-implemented `window.location.pathname.includes('report-view')`.
 * That missed two real cases:
 *   - hash-routed URLs (`/#/report-view/...`) where the path lives in `location.hash`;
 *   - guards/components that only looked at `Router.url`, which is still `'/'` during the
 *     very first navigation and therefore reported "not a public report" on a cold open.
 * Checking pathname + hash + an explicit router URL covers all of them.
 */

const PUBLIC_REPORT_SEGMENTS = ['report-view', 'report-preview'];

/** True when `url` (a router URL or raw path) points at a public report link. */
export function isPublicReportUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return PUBLIC_REPORT_SEGMENTS.some(segment => url.includes(segment));
}

/**
 * True when the *browser address bar* is on a public report link. Safe on the server and
 * safe before the Angular router has completed its first navigation.
 *
 * @param routerUrl optional `Router.url`, checked as well so in-app navigations are covered
 *                  even before `location` is updated.
 */
export function isPublicReportLocation(routerUrl?: string | null): boolean {
  if (isPublicReportUrl(routerUrl)) {
    return true;
  }
  if (typeof window === 'undefined' || !window.location) {
    return false;
  }
  return isPublicReportUrl(window.location.pathname) || isPublicReportUrl(window.location.hash);
}
